<template>
  <div class="m-page">
    <div class="m-back">
      <el-button link type="primary" @click="$router.push(mPath('dispatch'))">← 返回</el-button>
    </div>

    <p class="m-intro">勾选配料已完结且尚未加入其他派工单的订单明细，生成派工单。</p>

    <div v-loading="loading" class="m-form-wrap">
      <el-form label-position="top">
        <el-form-item label="指派人员（可选）">
          <el-select v-model="form.assigned_user_id" clearable filterable placeholder="未指派则为草稿" style="width: 100%">
            <el-option
              v-for="u in users"
              :key="u.user_id"
              :label="u.full_name || u.username"
              :value="u.user_id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.notes" type="textarea" :rows="2" placeholder="派工说明" />
        </el-form-item>
      </el-form>

      <div class="m-toolbar-mini">
        <el-input v-model="filterKw" clearable placeholder="筛选订单号、客户、料号…" />
      </div>

      <div class="m-pick-list">
        <label v-for="row in filteredItems" :key="row.order_item_id" class="m-pick">
          <input v-model="selectedIds" type="checkbox" :value="row.order_item_id" class="m-pick__cb" />
          <div class="m-pick__body">
            <div class="m-pick__t">{{ row.order_no }} · {{ row.customer_name || '—' }}</div>
            <div class="m-pick__m muted">{{ materialLine(row) }}</div>
            <div v-if="seasonTypeLine(row)" class="m-pick__meta muted">{{ seasonTypeLine(row) }}</div>
          </div>
        </label>
        <el-empty v-if="!loading && filteredItems.length === 0" description="当前没有可派工的明细" />
      </div>

      <div class="m-bottom">
        <span class="m-bottom__count">已选 {{ selectedIds.length }} 条</span>
        <el-button type="primary" size="large" :disabled="!canOperateDispatch || selectedIds.length === 0" :loading="submitting" @click="submit">
          生成派工单
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
defineOptions({ name: 'MobileDispatchCreate' })
import { useMobileWorkbench } from '@/composables/useMobileWorkbench'

const { mPath } = useMobileWorkbench()

import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { getCompletedOrderItemMatchings, createDispatchOrder } from '@/api/dispatchOrders'
import { getAssignableUsers } from '@/api/users'
import { useUserStore } from '@/store/user'
import { hasPermission } from '@/utils/permission'

const router = useRouter()
const userStore = useUserStore()
const loading = ref(false)
const submitting = ref(false)
const items = ref([])
const users = ref([])
const filterKw = ref('')
const selectedIds = ref([])
const canOperateDispatch = computed(() => hasPermission(userStore.user, 'orders.dispatch.edit'))

const form = reactive({
  assigned_user_id: null,
  notes: ''
})

const filteredItems = computed(() => {
  if (!filterKw.value.trim()) return items.value
  const k = filterKw.value.trim().toLowerCase()
  return items.value.filter(
    (r) =>
      field(r.order_no).includes(k) ||
      field(r.customer_name).includes(k) ||
      field(r.client_purchase_order).includes(k) ||
      field(r.material_matching?.material_identity_code).includes(k) ||
      field(r.material_matching?.warehouse_manual_code).includes(k) ||
      field(r.season_name).includes(k) ||
      field(r.season_year).includes(k) ||
      field(r.order_type_name).includes(k)
  )
})

function field(v) {
  return (v == null ? '' : String(v)).toLowerCase()
}

function materialLine(row) {
  const m = row.material_matching
  if (!m) return row.client_material_name || '—'
  const parts = [
    m.warehouse_manual_code || m.material_identity_code,
    m.material_category_name,
    m.material_color_name
  ].filter(Boolean)
  return parts.join(' · ') || row.client_material_name || '—'
}

function formatSeasonLine(row) {
  const name = row?.season_name
  const year = row?.season_year
  if (!name && (year === null || year === undefined || String(year).trim() === '')) return ''
  return [name, year].filter((x) => x != null && String(x).trim() !== '').join(' ')
}

function seasonTypeLine(row) {
  const s = formatSeasonLine(row)
  const t = (row?.order_type_name && String(row.order_type_name).trim()) || ''
  const parts = []
  if (s) parts.push(`季节 ${s}`)
  if (t) parts.push(`类型 ${t}`)
  return parts.join(' · ')
}

async function loadUsers() {
  const res = await getAssignableUsers({ per_page: 1000 })
  if (res.success) users.value = res.data || []
}

async function load() {
  loading.value = true
  try {
    const res = await getCompletedOrderItemMatchings()
    if (res.success) {
      items.value = Array.isArray(res.data) ? res.data : []
    } else {
      ElMessage.error(res.message || '加载失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '加载失败')
  } finally {
    loading.value = false
  }
}

async function submit() {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有生成派工单权限，请联系管理员')
    return
  }
  if (selectedIds.value.length === 0) {
    ElMessage.warning('请至少选择一条订单明细')
    return
  }
  submitting.value = true
  try {
    const res = await createDispatchOrder({
      order_item_ids: selectedIds.value,
      assigned_user_id: form.assigned_user_id,
      notes: form.notes || undefined
    })
    if (res.success) {
      ElMessage.success('派工单已生成；待桌面审核通过后，现场移动端列表才会显示并可执行。')
      router.replace(mPath('dispatch'))
    } else {
      ElMessage.error(res.message || '生成失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '生成失败')
  } finally {
    submitting.value = false
  }
}

onMounted(async () => {
  userStore.initFromStorage()
  await loadUsers()
  await load()
})
</script>

<style scoped>
.m-page {
  max-width: 720px;
  margin: 0 auto;
}

.m-back {
  margin-bottom: 8px;
}

.m-intro {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 12px;
  line-height: 1.5;
}

.m-form-wrap {
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.m-toolbar-mini {
  margin: 12px 0;
}

.m-pick-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 48vh;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 16px;
}

.m-pick {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
}

.m-pick__cb {
  margin-top: 4px;
  width: 18px;
  height: 18px;
}

.m-pick__body {
  flex: 1;
  min-width: 0;
}

.m-pick__t {
  font-weight: 600;
  font-size: 14px;
}

.m-pick__m {
  font-size: 13px;
  margin-top: 4px;
}

.m-pick__meta {
  font-size: 12px;
  margin-top: 2px;
}

.muted {
  color: #64748b;
}

.m-bottom {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.m-bottom__count {
  font-size: 14px;
  color: #334155;
}
</style>
